/* Industry packs (business categories): catalog access + the pure calculations behind the Industry page.
 * Catalog: contracts/industries.json (bilingual). Everything here is computed in the browser from data the
 * API already returns (employees, sites, settings), so there is no Go twin to keep in sync. */
import CATALOG_JSON from "../../../contracts/industries.json";
import type { AttendancePolicy, BusinessCategory, Employee, ISODate, SiteKind, WorkSite } from "../data/types";
import { hmToMin } from "./dates";
import { wageOf } from "./payroll";

export type Bi = [string, string];
/** "certs" | "sites" | "heat" | "shift" | "quota" are Industry-page engines; "/route" opens an app module. */
export type Engine = "certs" | "sites" | "heat" | "shift" | "quota" | `/${string}`;
export interface IndustryFeature { key: string; title: Bi; text: Bi; engine?: Engine; status: "live" | "planned" }
export interface IndustryCert { kind: string; ar: string; scope: "all" | "site"; match?: string[]; note?: Bi }
export interface QuotaRule { group: Bi; pct: number; minStaff: number; minWage: number; match: string[]; note?: Bi }
export interface Industry {
  key: BusinessCategory; icon: string; name: Bi; tagline: Bi; summary: Bi;
  stats: [string, Bi][]; pains: Bi[];
  sites: { label: Bi; kind: SiteKind; contract: boolean };
  heat: boolean;
  shift: { start: string; end: string; weekend: number[]; note: Bi };
  certs: IndustryCert[]; pay: Bi[]; quotas: QuotaRule[]; features: IndustryFeature[];
}
export interface HeatBanRule { from: string; to: string; start: string; end: string }

const CATALOG = CATALOG_JSON as unknown as { heatBan: HeatBanRule; categories: Industry[] };
export const INDUSTRIES: Industry[] = CATALOG.categories;
export const HEAT_BAN: HeatBanRule = CATALOG.heatBan;
export const industryOf = (k: string | null | undefined): Industry | undefined => INDUSTRIES.find((c) => c.key === k);

const norm = (s: string) => " " + s.toLowerCase().replace(/[^a-z0-9&]+/g, " ").trim() + " ";
/** Case-insensitive keyword match on a job title ("it " matches the word IT, "engineer" matches "Site Engineer"). */
export function titleMatches(title: string, keywords: string[]): boolean {
  const t = norm(title);
  return keywords.some((k) => {
    const kw = k.toLowerCase();
    return kw.endsWith(" ") ? t.includes(" " + kw.trim() + " ") : t.includes(kw.trim());
  });
}

const isActive = (e: Employee) => e.status !== "Terminated";
/** Employee ids on any Active site. */
export const onSiteIds = (sites: WorkSite[]): Set<number> => new Set(sites.filter((s) => s.status === "Active").flatMap((s) => s.employeeIds));

/* ---------- credential matrix ---------- */
export type CertState = "valid" | "expiring" | "expired" | "missing" | "na";
export interface CertCell { state: CertState; expiry?: ISODate; number?: string }
export interface CertRow { employee: Employee; cells: CertCell[] }
export interface CertMatrix { certs: IndustryCert[]; rows: CertRow[]; counts: Record<Exclude<CertState, "na">, number> }

export function certApplies(c: IndustryCert, e: Employee, siteIds: Set<number>): boolean {
  if (c.scope === "site" && !siteIds.has(e.id)) return false;
  return !c.match || titleMatches(e.title, c.match);
}

/** expiring = valid but within `warnDays` (default 30) of today; expiry on today counts as expired. */
export function certState(expiry: ISODate | undefined, today: ISODate, warnDays = 30): CertState {
  if (!expiry) return "missing";
  if (expiry <= today) return "expired";
  const ms = Date.parse(expiry + "T00:00:00Z") - Date.parse(today + "T00:00:00Z");
  return ms / 86400000 <= warnDays ? "expiring" : "valid";
}

/** Rows only for active employees that at least one certificate applies to, ordered by employee id. */
export function certMatrix(ind: Industry, employees: Employee[], sites: WorkSite[], today: ISODate): CertMatrix {
  const siteIds = onSiteIds(sites);
  const counts = { valid: 0, expiring: 0, expired: 0, missing: 0 };
  const rows: CertRow[] = [];
  for (const e of employees.filter(isActive).sort((a, b) => a.id - b.id)) {
    const cells = ind.certs.map((c): CertCell => {
      if (!certApplies(c, e, siteIds)) return { state: "na" };
      const doc = e.documents.find((d) => d.kind === c.kind);
      const state = certState(doc?.expiry, today);
      counts[state as keyof typeof counts]++;
      return { state, expiry: doc?.expiry, number: doc?.number };
    });
    if (cells.some((x) => x.state !== "na")) rows.push({ employee: e, cells });
  }
  return { certs: ind.certs, rows, counts };
}

/* ---------- profession quotas ---------- */
export interface QuotaResult {
  rule: QuotaRule; staff: number; saudis: number; counted: number; required: number; need: number;
  applies: boolean; pct: number; ok: boolean;
}
/** staff = active employees whose title matches; counted = Saudis paid ≥ minWage (full wage); required = ceil(pct% × staff). */
export function quotaCheck(rule: QuotaRule, employees: Employee[]): QuotaResult {
  const group = employees.filter((e) => isActive(e) && titleMatches(e.title, rule.match));
  const saudis = group.filter((e) => e.nationality === "SA");
  const counted = saudis.filter((e) => wageOf(e) >= rule.minWage).length;
  const staff = group.length;
  const required = Math.ceil((rule.pct * staff) / 100 - 1e-9);
  const applies = staff > 0 && staff >= rule.minStaff;
  const need = Math.max(0, required - counted);
  return { rule, staff, saudis: saudis.length, counted, required, need, applies, pct: staff ? Math.round((counted / staff) * 1000) / 10 : 0, ok: !applies || need === 0 };
}

/* ---------- work-site cost ---------- */
export interface SiteCost { headcount: number; wages: number; allowances: number; total: number; margin: number | null; marginPct: number | null }
/** Monthly labour cost of a site: full wages of assigned active employees + site allowance × headcount. */
export function siteCost(site: WorkSite, employees: Employee[]): SiteCost {
  const byId = new Map(employees.map((e) => [e.id, e]));
  const staff = site.employeeIds.map((id) => byId.get(id)).filter((e): e is Employee => !!e && isActive(e));
  const wages = staff.reduce((s, e) => s + wageOf(e), 0);
  const allowances = site.allowance * staff.length;
  const total = Math.round((wages + allowances) * 100) / 100;
  const margin = site.contractValue > 0 ? Math.round((site.contractValue - total) * 100) / 100 : null;
  return { headcount: staff.length, wages, allowances, total, margin, marginPct: margin == null ? null : Math.round((margin / site.contractValue) * 1000) / 10 };
}

/* ---------- summer midday work ban ---------- */
export interface HeatCheck {
  inSeason: boolean; seasonStart: ISODate; seasonEnd: ISODate; daysToSeason: number;
  overlapMinutes: number; shiftOverlaps: boolean;
}
/** Season = [from, to] inclusive of the current year (or next year once this year's season has passed). */
export function heatCheck(today: ISODate, policy: Pick<AttendancePolicy, "shiftStart" | "shiftEnd">, rule: HeatBanRule = HEAT_BAN): HeatCheck {
  const y = Number(today.slice(0, 4));
  const md = today.slice(5);
  const year = md > rule.to ? y + 1 : y;
  const seasonStart = `${year}-${rule.from}`, seasonEnd = `${year}-${rule.to}`;
  const inSeason = md >= rule.from && md <= rule.to;
  const days = Math.round((Date.parse(seasonStart + "T00:00:00Z") - Date.parse(today + "T00:00:00Z")) / 86400000);
  const s = hmToMin(policy.shiftStart), e = hmToMin(policy.shiftEnd);
  const overlapMinutes = Math.max(0, Math.min(e, hmToMin(rule.end)) - Math.max(s, hmToMin(rule.start)));
  return { inSeason, seasonStart, seasonEnd, daysToSeason: inSeason ? 0 : days, overlapMinutes, shiftOverlaps: overlapMinutes > 0 };
}

/** Whether the tenant's attendance policy already equals the industry's recommended shift. */
export const shiftApplied = (ind: Industry, p: AttendancePolicy) =>
  p.shiftStart === ind.shift.start && p.shiftEnd === ind.shift.end && [...p.weekendDays].sort().join() === [...ind.shift.weekend].sort().join();
